import {ref, computed, nextTick, onMounted} from 'vue'
import {notify} from '@kyvg/vue3-notification'
import UNRESTRICTED_BALANCE_ENTITY_TYPES from '@shared/constants/pages/requests/manage-funds/UnrestrictedWithdrawBalanceEntityTypes'

export function useWithdrawRequestForm({companyModelValue, adAccount, emit, createAdAccountTopUpRequest, pingBackend}) {
  let _uid = 0
  const defaultFormData = {
    entity_id: null,
    entity_type: null,
    amount: null,
    is_withdraw: true,
    is_asap: false,
    withdraw_full_balance: false,
  }
  const makeItem = () => ({...cloneSafe(defaultFormData), _uid: ++_uid})

  const formData = ref([makeItem()])
  const error = ref(null)
  const loading = ref(false)
  const selectAdAccountRef = ref()

  const canSubmit = computed(() =>
    formData.value.every(
      item => item.entity_id && item.amount !== null && item.amount !== '' && item.is_disabled === false,
    ),
  )
  const isCompanyBalanceError = computed(() => error.value?.errors?.company_balances?.length > 0)

  const init = () => {
    if (adAccount) {
      // The select preselects the exact account via :preselect-account.
      return
    }

    if (companyModelValue.value) {
      nextTick(() => {
        selectAdAccountRef.value?.[0]?.handleSearch?.('  ')
      })
    }
  }

  onMounted(() => {
    init()
  })

  const removeItem = index => {
    formData.value.splice(index, 1)
  }

  const setAccountBalance = (option, index) => {
    formData.value[index].balance = option.balance
  }
  const setIsOptionDisabled = (option, index) => {
    formData.value[index].is_disabled = option.is_disabled
  }
  const handleGetOption = (option, index) => {
    setAccountBalance(option, index)
    setIsOptionDisabled(option, index)
  }

  const onAdAccountChanged = (selectedAccount, index) => {
    let account = formData.value[index]

    account.entity_type = selectedAccount?.entity_type
    account.amount = null
    account.withdraw_full_balance = false
  }

  const onAmountChange = (amount, index) => {
    let account = formData.value[index]
    let localAmount = amount
    const skipBalanceCap = UNRESTRICTED_BALANCE_ENTITY_TYPES.includes(account.entity_type)

    if (!skipBalanceCap && localAmount > account.balance) {
      formData.value[index].amount = account.balance
      localAmount = account.balance
    }

    if (Number(account.balance) !== Number(localAmount)) {
      return (account.withdraw_full_balance = false)
    }

    account.withdraw_full_balance = true
  }

  const setAmount = (amount, index) => {
    let account = formData.value[index]

    account.amount = amount

    onAmountChange(amount, index)
  }

  const onWithdrawFullBalance = (switcherValue, index) => {
    let account = formData.value[index]

    if (!switcherValue) {
      return (account.amount = null)
    }

    account.amount = account.balance
  }

  const getRequestParams = () =>
    cloneSafe(formData.value).map(item => {
      delete item._uid
      delete item.is_disabled
      return {
        ...item,
        amount: Math.abs(item.amount) * -1,
      }
    })

  const openInvoiceModal = () => {
    emit('open-invoice-modal')
  }

  const handleCreate = async () => {
    loading.value = true

    try {
      await createAdAccountTopUpRequest(getRequestParams())

      pingBackend()
      emit('created')
      notify({type: 'success', text: 'Withdraw request submitted'})
    } catch (responseError) {
      error.value = responseError.data
      throw responseError
    } finally {
      loading.value = false
    }
  }

  const addNewItem = () => {
    formData.value.push(makeItem())

    nextTick(() => {
      selectAdAccountRef.value?.[formData.value.length - 1]?.handleSearch?.('  ')
    })
  }

  const resetFormAndReloadAccounts = () => {
    formData.value = [makeItem()]

    for (let i = 0; i < selectAdAccountRef.value.length; i++) {
      nextTick(() => {
        selectAdAccountRef.value[i].handleSearch?.('  ')
      })
    }
  }

  const isAmountDisabled = item => {
    if (item.balance === 0 && !UNRESTRICTED_BALANCE_ENTITY_TYPES.includes(item.entity_type)) {
      return true
    }

    return loading.value || item.withdraw_full_balance
  }

  const isFullBalanceWithdraw = item => {
    if (!item.entity_id || item.amount === null || item.amount === '') {
      return false
    }

    if (item.withdraw_full_balance) {
      return true
    }

    return Number(item.amount) === Number(item.balance)
  }

  return {
    UNRESTRICTED_BALANCE_ENTITY_TYPES,
    formData,
    error,
    loading,
    selectAdAccountRef,
    canSubmit,
    isCompanyBalanceError,
    removeItem,
    handleGetOption,
    onAdAccountChanged,
    onAmountChange,
    setAmount,
    onWithdrawFullBalance,
    openInvoiceModal,
    handleCreate,
    addNewItem,
    resetFormAndReloadAccounts,
    isAmountDisabled,
    isFullBalanceWithdraw,
  }
}
